<template>
  <div class="create-record">
    <h1>Create a Record</h1>

    <form @submit.prevent="submitForm">
      <!-- Record Name -->
      <div class="form-group">
        <label for="name">Name</label>
        <input
          type="text"
          id="name"
          v-model="record.name"
          required
          class="form-control"
        />
      </div>

      <!-- Record Type -->
      <div class="form-group">
        <label for="record_type_id">Type</label>
        <div class="type-selector">
          <select
            id="record_type_id"
            v-model.number="record.record_type_id"
            required
            class="form-control"
          >
            <option value="" disabled>Select a record type...</option>
            <option v-for="type in recordTypes" :key="type.id" :value="type.id">
              {{ type.name }}
            </option>
          </select>
          <button
            type="button"
            @click="showCreateTypeDialog = true"
            class="create-type-button"
            title="Create new record type"
          >
            + New Type
          </button>
        </div>
      </div>

      <!-- Validation Errors -->
      <div v-if="validationErrors.length" class="form-group alert-container">
        <div class="alert alert-danger">
          <p v-for="error in validationErrors" :key="error">{{ error }}</p>
        </div>
      </div>

      <!-- Submit -->
      <button type="submit" class="submit-button">Create Record</button>
    </form>

    <!-- Create Record Type Dialog -->
    <div v-if="showCreateTypeDialog" class="modal-overlay" @click.self="showCreateTypeDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Create New Record Type</h3>
          <button type="button" class="close-btn" @click="showCreateTypeDialog = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="type-name">Name</label>
            <input
              type="text"
              id="type-name"
              v-model="newRecordType.name"
              class="form-control"
              placeholder="e.g., Sample, Analysis, etc."
            />
          </div>
          <div class="form-group">
            <label for="type-desc">Description (optional)</label>
            <textarea
              id="type-desc"
              v-model="newRecordType.description"
              class="form-control"
              placeholder="Describe this record type..."
              rows="4"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="showCreateTypeDialog = false">Cancel</button>
          <button type="button" class="save-btn" @click="createRecordType">Create</button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import apiService from "@/services/apiService";
import { API_ENDPOINTS } from "@/services/config";
import { mapState } from "vuex";

const FORM_CACHE_KEY = "record_form";
const CACHE_EXPIRATION = 10 * 60 * 1000;

export default {
  data() {
    return {
      record: {
        name: "",
        record_type_id: "",
      },
      recordTypes: [],
      showCreateTypeDialog: false,
      newRecordType: {
        name: "",
        description: "",
      },
      validationErrors: [],
      loading: false,
    };
  },

  computed: {
    ...mapState(["loggedInUser"]),
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  mounted() {
    this.loadCachedFormData();
    this.fetchRecordTypes();
  },

  methods: {
    async fetchRecordTypes() {
      try {
        const response = await apiService.fetchResources(
          API_ENDPOINTS.recordTypes,
          this.loggedInUser?.entra_id
        );
        this.recordTypes = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error fetching record types:", error);
        this.toast.error("Failed to load record types");
      }
    },

    loadCachedFormData() {
      const cachedData = JSON.parse(localStorage.getItem(FORM_CACHE_KEY));
      if (!cachedData) return;

      const { data, timestamp } = cachedData;
      if (Date.now() - timestamp < CACHE_EXPIRATION) {
        this.record = {
          ...this.record,
          ...data,
        };
      } else {
        localStorage.removeItem(FORM_CACHE_KEY);
      }
    },

    cacheFormData() {
      localStorage.setItem(
        FORM_CACHE_KEY,
        JSON.stringify({ data: this.record, timestamp: Date.now() })
      );
    },

    validateForm() {
      this.validationErrors = [];

      if (!this.record.name) this.validationErrors.push("name is required");
      if (!this.record.record_type_id) this.validationErrors.push("record type is required");

      return this.validationErrors.length === 0;
    },

    async createRecordType() {
      if (!this.newRecordType.name.trim()) {
        this.toast.error("Record type name is required");
        return;
      }

      try {
        const payload = {
          name: this.newRecordType.name,
          description: this.newRecordType.description,
        };

        const response = await apiService.createResource(
          API_ENDPOINTS.recordTypes,
          payload,
          this.loggedInUser.entra_id
        );

        this.recordTypes.push(response);
        this.record.record_type_id = response.id;
        this.showCreateTypeDialog = false;
        this.newRecordType = { name: "", description: "" };
        this.toast.success("Record type created successfully!");
      } catch (error) {
        console.error("Failed to create record type:", error);
        this.toast.error("Failed to create record type!");
      }
    },

    async submitForm() {
      if (!this.validateForm()) {
        this.toast.error("Form is invalid!");
        return;
      }

      this.loading = true;
      try {
        const payload = {
          name: this.record.name,
          record_type_id: this.record.record_type_id,
          user_id: this.loggedInUser.user_id,
        };

        // Endpoint: POST /records
        const response = await apiService.createResource(
          API_ENDPOINTS.records,
          payload,
          this.loggedInUser.entra_id
        );

        this.toast.success("Record created successfully!");
        localStorage.removeItem(FORM_CACHE_KEY);

        // API returns { id } on success, but may be { node_id } as well
        const recordId = response.id || response.node_id;
        this.$router.push({
          name: "RecordDetailPage",
          params: { id: recordId },
        });
      } catch (error) {
        console.error("Failed to create record:", error);
        this.toast.error("Failed to create record!");
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    record: {
      handler() {
        this.cacheFormData();
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.create-record {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);

  h1 {
    margin-bottom: 2rem;
    text-align: center;
  }

  .type-selector {
    display: flex;
    gap: 10px;
    align-items: center;

    select {
      flex: 1;
    }

    .create-type-button {
      padding: 8px 12px;
      background-color: $primary-color;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      white-space: nowrap;
      font-size: 14px;

      &:hover {
        background-color: darken($primary-color, 10%);
      }
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .modal-content {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e0e0e0;

      h3 {
        margin: 0;
        color: $primary-color;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #666;

        &:hover {
          color: #000;
        }
      }
    }

    .modal-body {
      padding: 20px;

      .form-group {
        margin-bottom: 15px;

        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #333;
        }

        input,
        textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-family: inherit;

          &:focus {
            outline: none;
            border-color: $primary-color;
          }
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 20px;
      border-top: 1px solid #e0e0e0;

      button {
        padding: 8px 16px;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-size: 14px;

        &.cancel-btn {
          background-color: #e0e0e0;
          color: #333;

          &:hover {
            background-color: #d0d0d0;
          }
        }

        &.save-btn {
          background-color: $primary-color;
          color: white;

          &:hover {
            background-color: darken($primary-color, 10%);
          }
        }
      }
    }
  }
}
</style>